import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { NodeModel } from '../../../../models/node-model';
import { MeasureService, IMeasureInputAM, IMeasureEntryAM, MeasureEntryAM } from '../../services/measure-service';
import { CustomerMunicipality } from '../../../municipalities/municipality-service';
import { IScopeAM } from '../../../scopes/scope-service';
import { find, isEqual, uniqBy } from 'lodash';
import { BindingSignaler } from 'aurelia-templating-resources';
import {AccountingDimensionValue} from "../../../accounting-dimensions/accounting-dimension-api";
import { AppSettings } from '../../../../utils/appsettings';
import { IFormItem, MeasureInputGroup } from '../../measure-input-group';

@autoinject
export class MeasureInputGroupView {

	@bindable measureInputs: IMeasureInputAM[];
	@bindable comparisonMeasureInputs: IMeasureInputAM[];
	@bindable partialMemberServiceSelections: { [customerMunicipalityId: string]: string[] } = { }; // key: customerMunicipalityId, value: selected service-id's
	@bindable scope: IScopeAM;
	@bindable customerId: string;
	@bindable showInputsForServiceLevel: boolean;
	@bindable customerMunicipalities: CustomerMunicipality[];
	@bindable getComparisonCustomerMunicipalityId: (customerMunicipalityId: any) => string;
	@bindable disableServiceLevelInputs: (group: MeasureInputGroup, formItem: IFormItem) => void; // Disable service level inputs for a service if they are already shown

	@bindable measureInputGroup: MeasureInputGroup;

	constructor(private measureService: MeasureService, private signaler: BindingSignaler) {
		this.measureInputs = [];
	}

	async comparisonMeasureInputsChanged(newValue){
		this.signaler.signal('comparisonEntryChanged');
	}

	allInputsForCurrentService(formItem: IFormItem): IMeasureInputAM[] {
		return this.measureInputs.filter(item => item.serviceId === formItem.serviceId);		
	}

	inputsForServiceLevel(formItem: IFormItem): IMeasureInputAM[] {
		return this.allInputsForCurrentService(formItem).filter(item => item.forServiceTotal || item.forServiceByMunicipalities || item.existingEntries.some(me => !me.costCenterId && me.serviceId === formItem.serviceId));
	}

	inputsForFormItem(formItem: IFormItem): IMeasureInputAM[] {
		const inputs = this.allInputsForCurrentService(formItem).filter((item) => {
			return item.forCostCentersTotal || item.forCostCentersByMunicipalities || item.existingEntries.some(me => !!me.costCenterId);
		});

		return inputs;
	}

	@computedFrom('measureInputGroup', 'measureInputs')
	get measureInputGroupHasInputs(): boolean {
		switch (this.measureInputGroup.showInputsForServiceLevel) {
			case true:
				return this.measureInputGroup.formItems.some(item => this.inputsForServiceLevel(item).length > 0);
			case false:
				return this.measureInputGroup.formItems.some(item => this.inputsForFormItem(item).length > 0);
		}
	}

	@computedFrom('measureInputGroup')
	get groupTitle(): string {
		return this.measureInputGroup.root.pathSegments[this.measureInputGroup.root.pathSegments.length - 1];
	}

	getInputsForFormItem(formItem: IFormItem): IMeasureInputAM[] {
		return this.allInputsForCurrentService(formItem).filter(item => item.forCostCentersTotal || item.forCostCentersByMunicipalities || item.existingEntries.some(me => me.costCenterId === formItem.costCenterId && isEqual(me.dimensionValueIds, formItem.dimensionValueIds)));
	}

	get userHasReadOnlyAccess(): boolean {
		return !AppSettings.roles.some(r => r === 'GlobalAdministrator' || r === 'GlobalCustomerAdministrator' || r === 'Administrator' || r === 'DomainUser');
	}

	getMeasureEntryForInput(
		measureId: string,
		customerMunicipalityId: string = null,
		serviceId: string,
		costCenterId: string = null,
		dimensionValueIds: string[] = null): MeasureEntryAM {

		// First get measure inputs for current service and then filter for correct cost center
		const measureInputs = this.measureInputs.filter(item => item.measureId === measureId && item.serviceId === serviceId);
		const measureInput = measureInputs.find(input => input.existingEntries.find(e => e.costCenterId == costCenterId)) ?? measureInputs[0];

		if (!measureInput.existingEntries) {
			const newEntry = this.createNewMeasureEntry(measureInput, customerMunicipalityId, costCenterId, dimensionValueIds);
			newEntry.isCollected = newEntry.isEntryMeasured(measureInput);
			return newEntry;
		}

		const entry = find(measureInput.existingEntries, e => {
			return e.costCenterId === costCenterId
				&& e.customerMunicipalityId === customerMunicipalityId
				&& e.serviceId === serviceId
				&& AccountingDimensionValue.areValuesEqual(e.dimensionValueIds, dimensionValueIds);
		});

		if (entry) {
			const measureEntry = this.createNewMeasureEntry(measureInput, customerMunicipalityId, costCenterId, dimensionValueIds);
			measureEntry.value = entry.value;
			measureEntry.isCollected = measureEntry.isEntryMeasured(measureInput);
			return measureEntry;
		} else {
			const measureEntry = this.createNewMeasureEntry(measureInput, customerMunicipalityId, costCenterId, dimensionValueIds);
			measureEntry.isCollected = measureEntry.isEntryMeasured(measureInput);
			return measureEntry;
		}
	}

	getComparisonMeasureEntryForInput (
		measureId: string,
		customerMunicipalityId: string = null,
		serviceId: string,
		costCenterId: string = null,
		dimensionValueIds: string[] = null): MeasureEntryAM {

		const comparisonInput = this.comparisonMeasureInputs.find(item => item.measureId === measureId && item.serviceId === serviceId && item.existingEntries.find(e => e.costCenterId == costCenterId));
		if (!comparisonInput || !comparisonInput.existingEntries) { return null; }

		let comparisonCustomerMunicipalityId = this.getComparisonCustomerMunicipalityId({ customerMunicipalityId });
		
		const comparisonEntry = find(comparisonInput.existingEntries, e => {
			return e.costCenterId === costCenterId
				&& e.customerMunicipalityId === comparisonCustomerMunicipalityId
				&& e.serviceId === serviceId
				&& AccountingDimensionValue.areValuesEqual(e.dimensionValueIds, dimensionValueIds);
		});		

		if (!comparisonEntry) { return null; }

		const newEntry = this.createNewMeasureEntry(comparisonInput, comparisonCustomerMunicipalityId, costCenterId, dimensionValueIds);
		newEntry.value = comparisonEntry.value;

		return newEntry;
	}

	insertMeasureValue(measureEntry: IMeasureEntryAM) {
		this.measureService.insertMeasureValue(measureEntry, this.customerId);
	}

	private createNewMeasureEntry(
		measure: IMeasureInputAM,
		customerMunicipalityId: string = null,
		costCenterId: string = null,
		dimensionValueIds: string[] = null): MeasureEntryAM {

		const vm = new MeasureEntryAM(measure);
		vm.costCenterId = costCenterId;
		vm.customerMunicipalityId = customerMunicipalityId;
		vm.scopeId = this.scope.id;
		vm.dimensionValueIds = dimensionValueIds;
		return vm;
	}
}

export class CustomerMunicipalityWithServiceInvolvementField extends CustomerMunicipality {
	
	IsInvolvedInCurrentService: boolean;
}
